import { defineStore } from 'pinia'
import { api, extractError } from '@/api/client'
import type { KeysResponse, VpnKey } from '@/types'
import { useServerHealthStore } from '@/stores/serverHealth'

interface State {
  items: VpnKey[]
  loading: boolean
  error: string | null
  lastFetch: number
}

export const useKeysStore = defineStore('keys', {
  state: (): State => ({
    items: [],
    loading: false,
    error: null,
    lastFetch: 0,
  }),
  actions: {
    /**
     * Загружает ключи. По умолчанию кеширует на 5 сек — это защищает от
     * множественных параллельных вызовов (mounted + auto-refresh + успешная активация).
     * force=true игнорирует кеш.
     */
    async fetch(force = false): Promise<void> {
      if (!force && Date.now() - this.lastFetch < 5000) return
      this.loading = true
      this.error = null
      try {
        const { data } = await api.get<KeysResponse>('/api/user/keys')
        this.items = data.keys || []
        useServerHealthStore().mergeBootstrap(data.server_health)
        this.lastFetch = Date.now()
      } catch (e) {
        this.error = extractError(e, 'Не удалось загрузить ключи')
      } finally {
        this.loading = false
      }
    },

    /** Пересобрать ключи (инвалидация Redis-кеша на бэке). */
    async rebuild(): Promise<{ success: boolean; keys_count?: number; message?: string }> {
      const { data } = await api.post('/api/user/keys', { action: 'rebuild' })
      if (data.success) {
        await this.fetch(true)
      }
      return data
    },

    async setGlobalFingerprint(
      value: string,
      consent?: string,
    ): Promise<{
      success: boolean
      global_fingerprint?: string
      changed?: boolean
      message?: string
    }> {
      const { data } = await api.post('/api/user/keys', {
        action: 'set_global_fingerprint',
        global_fingerprint: value,
        fingerprint_risk_consent: consent ?? '',
      })
      return data
    },

    async setIncludeInSublink(
      keyId: number,
      include: boolean,
    ): Promise<{ success: boolean; include_in_sublink?: boolean; message?: string }> {
      const { data } = await api.post('/api/user/keys', {
        action: 'set_include_in_sublink',
        id: keyId,
        include_in_sublink: include,
      })
      if (data.success) {
        const item = this.items.find((k) => k.id === keyId)
        if (item) {
          item.include_in_sublink = include
        }
      }
      return data
    },

    /** Удалить manual-ключ (POST action=delete). */
    async remove(keyId: number): Promise<{ success: boolean; purged?: number; panel_errors?: number; message?: string }> {
      const { data } = await api.post('/api/user/keys', { action: 'delete', id: keyId })
      if (data.success) {
        await this.fetch(true)
      }
      return data
    },
  },
})
